import { CustomSelect, Label, SelectOption, TextInput } from '@consigli/facade';
import { Language } from '@consigli/types';
import { FC, useCallback, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PackageFormData } from './types';

interface PackageConfigurationFormProps {
  validateName: (value: string) => boolean | string;
}

export const PackageConfigurationForm: FC<PackageConfigurationFormProps> = ({ validateName }) => {
  const { t } = useTranslation();
  const { control } = useFormContext<PackageFormData>();

  const { field: packageNameField, fieldState: packageNameFieldState } = useController({
    name: 'packageName',
    control,
    rules: {
      required: t('package-creation.package-name-required', 'Package name is required'),
      validate: validateName,
    },
  });

  const { field: languageField } = useController({
    name: 'language',
    control,
  });

  const { field: reportTypeField, fieldState: reportTypeFieldState } = useController({
    name: 'reportType',
    control,
    rules: {
      required: t('package-creation.reports.report-type-required', 'Report type is required'),
    },
  });

  const { field: locationField, fieldState: locationFieldState } = useController({
    name: 'location',
    control,
    rules: {
      required: t('package-creation.reports.location-required', 'Location is required'),
    },
  });

  const languageOptions = useMemo(
    () => [
      { value: Language.EN, label: t('language.english', 'English') },
      { value: Language.NB, label: t('language.norwegian', 'Norwegian') },
      { value: Language.SV, label: t('language.swedish', 'Swedish') },
      { value: Language.DE, label: t('language.german', 'German') },
    ],
    [t],
  );

  const selectedLanguageValue = useMemo(() => {
    const option = languageOptions.find((opt) => opt.value === languageField.value);
    return option ? [option] : [];
  }, [languageField.value, languageOptions]);

  const handleLanguageChange = useCallback(
    (options: SelectOption[]) => {
      languageField.onChange((options[0]?.value as Language) || Language.EN);
    },
    [languageField],
  );

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="package-name">{t('package-creation.package-name', 'Package Name')} *</Label>
        <TextInput
          id="package-name"
          value={packageNameField.value}
          onChange={packageNameField.onChange}
          placeholder={t('package-creation.package-name-placeholder', 'Enter package name')}
        />
        {packageNameFieldState.invalid && (
          <div className="text-red-700 text-sm mt-1">
            {packageNameFieldState.error?.message as string}
          </div>
        )}
      </div>

      <div>
        <Label htmlFor="report-type">
          {t('package-creation.reports.report-type', 'Report Type')} *
        </Label>
        <TextInput
          id="report-type"
          value={reportTypeField.value}
          onChange={reportTypeField.onChange}
          placeholder={t('package-creation.reports.report-type-placeholder', 'Enter report type')}
        />
        {reportTypeFieldState.invalid && (
          <div className="text-red-700 text-sm mt-1">
            {reportTypeFieldState.error?.message as string}
          </div>
        )}
      </div>

      <div>
        <Label htmlFor="location">{t('package-creation.reports.location', 'Location')} *</Label>
        <TextInput
          id="location"
          value={locationField.value}
          onChange={locationField.onChange}
          placeholder={t('package-creation.reports.location-placeholder', 'Enter location')}
        />
        {locationFieldState.invalid && (
          <div className="text-red-700 text-sm mt-1">
            {locationFieldState.error?.message as string}
          </div>
        )}
      </div>

      <div>
        <Label htmlFor="language">{t('package-creation.language', 'Language')}</Label>
        <CustomSelect
          options={languageOptions}
          value={selectedLanguageValue}
          onChange={handleLanguageChange}
          placeholder={t('package-creation.select-language', 'Select language')}
        />
      </div>
    </div>
  );
};
